import React, {
  ComponentProps,
  RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';
import { Modal, TouchableOpacity } from 'react-native';

import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { ContactType } from '@components/Invite/Contact';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import {
  PopupProjectsFilterDateRangeV3,
  PopupProjectsFilterV3,
} from '@components/Web/PopupProjectsFilterV3.web';
import { Team } from '@graphql/generated';
import { useOutsideClick } from '@hooks/useOutsideClick.web';

import { getScaledSize, SCALED_DIMENSIONS } from '../../../themes/theme';

type PositionTuple = ['left' | 'right', 'top' | 'bottom'];

type Position = PositionTuple | 'top' | 'bottom' | 'left' | 'right';

type Props = Omit<ComponentProps<typeof TouchableOpacity>, 'onPress'> & {
  title: string;
  onDismiss?: any;
  onSelectTeam?: (
    item: Pick<Team, 'id' | 'name' | 'avatar' | 'usersCount' | 'personal'>
  ) => void;
  onSelectOwner?: (ids: ContactType[]) => void;
  onSelectMember?: (ids: ContactType[]) => void;
  boxProps: Partial<{
    position: Position;
    offset: [number, number] | number;
  }>;
  selectedOwners?: ContactType[];
  selectedMembers?: ContactType[];
  selectedTeamIds?: string[];
  selectedDueDateStrList?: string[];
  onSelectedDueDateStrList?: (x: string[]) => void;
  onSelectDuedate?: (dr: DateRange) => void;
  selectedDuedate?: DateRange;
  onClearTeams?: () => void;
};

export const PopupProjectsFiltersButtonWeb = ({
  onDismiss,
  onSelectTeam,
  onSelectOwner,
  onSelectMember,
  boxProps: { position, offset, ...popupPropsRest },
  title,
  selectedOwners = [],
  selectedMembers = [],
  selectedTeamIds = [],
  selectedDueDateStrList = [],
  onSelectedDueDateStrList,
  onSelectDuedate,
  selectedDuedate,
  onClearTeams,
}: Props) => {
  const [visible, setVisible] = useState(false);

  const togglePopup = () => setVisible((v) => !v);

  const isEventPointInArea = (
    ref: RefObject<HTMLDivElement>,
    event: MouseEvent
  ) => {
    const rect = ref?.current?.getBoundingClientRect();
    return (
      rect &&
      event.x > rect.x &&
      event.x < rect.x + rect.width &&
      event.y > rect.y &&
      event.y < rect.y + rect.height
    );
  };

  const ref2 = useRef<HTMLDivElement>();

  const iconCloseRef = useRef<HTMLDivElement>();

  const isEventPointInModal = (event: MouseEvent) => {
    return isEventPointInArea(ref2, event);
  };

  const isEventPointInCloseIcon = (event: MouseEvent) => {
    return isEventPointInArea(iconCloseRef, event);
  };

  const clearState = () => {
    onSelectOwner?.([]);
    onSelectMember?.([]);
    onClearTeams?.();
    onSelectedDueDateStrList?.([]);
    onSelectDuedate?.({});
  };

  const handleOutsideClick = useCallback((event: MouseEvent) => {
    if (!isEventPointInModal(event)) {
      if (isEventPointInCloseIcon(event)) {
        clearState();
      } else {
        setVisible(false);
      }
    }
  }, []);

  const ref = useOutsideClick<HTMLDivElement>(handleOutsideClick);

  const [initialSection, setInitialSection] = useState<
    'owner' | 'team' | 'member' | 'duedate'
  >('owner');

  const [layout, setLayout] = useState<
    { width: number; height: number } | undefined
  >();

  const [currentPanel, setCurrentPanel] = useState<'main' | 'dates'>('main');

  const handleSelectDatesClick = () => {
    setInitialSection('duedate');
    setCurrentPanel('dates');
  };

  const handleDateRangeSelected = (dr: DateRange) => {
    onSelectedDueDateStrList?.([]);
    onSelectDuedate?.(dr);
    setCurrentPanel('main');
  };

  const badgeNumber =
    selectedOwners?.length +
    selectedMembers?.length +
    selectedTeamIds?.length +
    selectedDueDateStrList?.length +
    (selectedDuedate?.start || selectedDuedate?.end ? 1 : 0);

  const [mouseOver, setMouseOver] = useState(false);

  const adjustBounds = (
    position: Props['boxProps']['position'] = 'bottom',
    offset: Props['boxProps']['offset'] = 0,
    client: DOMRect,
    popupLayout: { width: number; height: number }
  ) => {
    const [offsetLeftRight, offsetTopBottom] =
      typeof offset === 'number' ? [offset, offset] : offset;

    const normalizedPosition = (typeof position !== 'string' && position) ||
      ((position === 'left' || position === 'right') && [position, 'bottom']) ||
      ((position === 'top' || position === 'bottom') && ['left', position]) || [
        'left',
        'bottom',
      ];

    const [leftRight, topBottom] = normalizedPosition;
    const bounds = {
      top:
        topBottom === 'top'
          ? client.top - popupLayout.height - offsetTopBottom
          : client.top + client.height + offsetTopBottom,
      left:
        leftRight === 'left'
          ? client.left + client.width - popupLayout.width - offsetLeftRight
          : client.left + offsetLeftRight,
    };

    return bounds;
  };

  const adjustedBounds =
    ref.current &&
    layout &&
    adjustBounds(position, offset, ref.current.getBoundingClientRect(), layout);

  const onModalDismiss = () => {
    setInitialSection('owner');
    setCurrentPanel('main');
    onDismiss?.();
    setVisible(false);
  };

  const backgroundColor1 = () => {
    if (mouseOver || badgeNumber !== 0 || visible) return 'grey01';
    return 'white';
  };

  return (
    <Box ref={ref}>
      <TouchableOpacity
        accessibilityLabel={title}
        onPress={() => {
          togglePopup();
        }}>
        <Box
          height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
          flexDirection='row'
          borderWidth={1}
          px='m'
          py='xs'
          onMouseEnter={() => {
            setMouseOver(true);
          }}
          onMouseLeave={() => {
            setMouseOver(false);
          }}
          backgroundColor={backgroundColor1()}
          borderColor='grey02'
          borderRadius='xs'
          alignItems='center'
          justifyContent='center'>
          <Box alignItems='center' justifyContent='center' style={{ flex: 1 }}>
            <Text variant='webBodySecondary' color='textPrimary'>
              {title}
            </Text>
          </Box>
          {badgeNumber > 0 ? (
            <Box
              marginLeft='xs'
              width={20}
              height={20}
              alignItems='center'
              justifyContent='center'
              backgroundColor='greenSecondary'
              style={{ borderRadius: 10 }}>
              <Text variant='webBodyHintSubline' color='white'>
                {badgeNumber}
              </Text>
            </Box>
          ) : (
            <Box
              width={16}
              height={16}
              marginLeft='xs'
              alignItems='center'
              justifyContent='center'
              pointerEvents='none'>
              <Icon variant='s' name='ChevronDown' color='textPrimary' />
            </Box>
          )}
          {badgeNumber > 0 ? (
            <Box ref={iconCloseRef}>
              <Icon
                marginLeft='xs'
                variant='xxxs'
                name='Close'
                onPress={() => {
                  clearState();
                }}
                color='black'
              />
            </Box>
          ) : (
            <Box />
          )}
        </Box>
      </TouchableOpacity>

      {!layout && (
        <Box
          left='-200vw'
          position='absolute'
          visible={!layout}
          onLayout={({ nativeEvent: { layout } }) => {
            setLayout((curr) => curr ?? layout);
          }}>
          <PopupProjectsFilterV3
            selectedOwners={selectedOwners}
            {...popupPropsRest}
          />
        </Box>
      )}
      <Modal
        visible={visible}
        transparent
        onDismiss={() => onModalDismiss()}
        onRequestClose={() => onModalDismiss()}>
        <Box ref={ref2} style={{ ...adjustedBounds }} width={layout?.width}>
          <Box flexShrink={1}>
            {currentPanel === 'main' ? (
              <PopupProjectsFilterV3
                selectedOwners={selectedOwners}
                {...popupPropsRest}
                onSelectOwner={(ids) => {
                  onSelectOwner && onSelectOwner(ids);
                }}
                onSelectTeam={(item) => {
                  onSelectTeam && onSelectTeam(item);
                }}
                selectedTeamIds={selectedTeamIds}
                onSelectMember={(ids) => {
                  onSelectMember && onSelectMember(ids);
                }}
                selectedMembers={selectedMembers}
                selectedDueDateStrList={selectedDueDateStrList}
                onSelectedDueDateStrList={onSelectedDueDateStrList}
                selectedDueDateRange={selectedDuedate}
                onSelectDueDateRange={onSelectDuedate}
                onClearTeams={onClearTeams}
                onPressSelectDates={handleSelectDatesClick}
                initiallyOpenSection={initialSection}
              />
            ) : (
              <PopupProjectsFilterDateRangeV3
                selectedDueDateRange={selectedDuedate}
                onSelectDueDateRange={(dr) => handleDateRangeSelected(dr)}
                isRangeSelector={false}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
